<template>
  <button
    class="tile"
    type="button"
    :disabled="disabled"
    v-bind="$attrs"
    @click="onClick"
  >
    <!-- @slot left visual, overrides the icon prop -->
    <span v-if="$slots['left-icon'] || icon" class="tile__visual" :style="visualStyle">
      <slot name="left-icon">
        <app-material-icon
          :name="icon"
          :size="20"
          :color="resolvedIconColor"
          :filled="iconFilled"
        />
      </slot>
    </span>

    <span class="tile__content">
      <span class="tile__heading">
        <!-- @slot heading content, overrides the label prop -->
        <slot name="heading">
          <span class="tile__label">{{ label }}</span>
        </slot>
        <!-- @slot optional badge displayed next to the label -->
        <slot name="badge"></slot>
      </span>
      <!-- @slot caption content, overrides the caption prop -->
      <span v-if="$slots['caption'] || caption" class="tile__caption">
        <slot name="caption">{{ caption }}</slot>
      </span>
    </span>
  </button>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue'

import type { ThemeColors } from '@/plugins/styles/colors'

/**
 * @displayName Tile
 * A large clickable button with a label, optional caption and optional leading icon.
 */
export default defineComponent({
  name: 'AppTile',
  inheritAttrs: false,
  props: {
    /**
     * Main heading text of the tile
     */
    label: {
      type: String,
      default: '',
    },
    /**
     * Secondary helper text displayed under the label
     */
    caption: {
      type: String,
      default: '',
    },
    /**
     * Material icon name displayed in the leading visual
     */
    icon: {
      type: String,
      default: '',
    },
    /**
     * Theme color token of the leading icon
     * @example "icon-primary", "icon-info"
     */
    iconColor: {
      type: String as PropType<ThemeColors>,
      default: 'icon-primary',
    },
    /**
     * Adds the "filled" prop to the icon
     */
    iconFilled: {
      type: Boolean,
      default: false,
    },
    /**
     * Theme color token of the leading visual background
     * @example "surface-grey-subtle", "surface-info-subtle"
     */
    iconBackgroundColor: {
      type: String as PropType<ThemeColors>,
      default: 'surface-grey-subtle',
    },
    /**
     * Disables the tile
     */
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click'],
  computed: {
    resolvedIconColor (): string {
      return this.disabled ? 'rgb(var(--v-theme-icon-disabled))' : `rgb(var(--v-theme-${this.iconColor}))`
    },
    visualStyle (): Record<string, string> {
      return { backgroundColor: `rgb(var(--v-theme-${this.iconBackgroundColor}))` }
    },
  },
  methods: {
    onClick (event: MouseEvent) {
      this.$emit('click', event)
    },
  },
})
</script>

<style scoped lang="scss">
.tile {
  position: relative;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  column-gap: 16px;
  width: 100%;
  padding: 16px;
  border: 1px solid rgb(var(--v-theme-border-secondary));
  border-radius: 8px;
  background-color: rgb(var(--v-theme-surface-primary));
  text-align: left;
  cursor: pointer;

  // overlay used for hover / focus / pressed background tint
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
  }

  &:hover:enabled,
  &:focus-visible:enabled {
    border-color: rgb(var(--v-theme-border-secondary-hover));

    &::after {
      background-color: rgba(var(--v-theme-surface-hover));
    }
  }

  &:active:enabled {
    border-color: rgb(var(--v-theme-border-secondary-hover));

    &::after {
      background-color: rgba(var(--v-theme-surface-pressed));
    }
  }

  &:focus-visible:enabled {
    outline: none;
    box-shadow: 0 0 0 3px rgb(var(--v-theme-sky-400));
  }

  &:disabled {
    border-color: rgb(var(--v-theme-border-disabled));
    background-color: rgb(var(--v-theme-surface-disabled));
    cursor: default;

    .tile__label,
    .tile__caption {
      color: rgb(var(--v-theme-text-disabled));
    }
  }
}

.tile__visual {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 4px;
  overflow: hidden;
}

.tile__content {
  flex: 1 0 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
}

.tile__heading {
  display: flex;
  align-items: center;
  column-gap: 8px;
}

.tile__label {
  overflow: hidden;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: rgb(var(--v-theme-text-primary));
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tile__caption {
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  color: rgb(var(--v-theme-text-tertiary));
}
</style>
